import React, { useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { get_app_logo, s3baseUrl } from "src/config/config";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrCard from "src/design-system/components/GrCard";
import GrCardHeader from "src/design-system/components/GrCardHeader";
import GrChatListRow from "src/design-system/components/GrChatListRow";
import GrEmptyState from "src/design-system/components/GrEmptyState";
import GrIcon from "src/design-system/components/GrIcon";
import GrIconButton from "src/design-system/components/GrIconButton";
import { GrMenu, GrMenuItem } from "src/design-system/components/GrMenu";

// Card padding is on the headings only, so the rows (their own 16 padding) run edge to edge.
const cardSx = {
  p: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};
// Card titles (18, teal, as on every card) lined up with the rows' avatars (16 in); 20 above, 8 below.
// "Groups" starts a second part: a hairline above it separates it from General Community.
const headerSx = { px: gr.space[4], pt: gr.space[5], pb: gr.space[2], mb: 0 };
const groupsHeaderSx = {
  ...headerSx,
  mt: gr.space[2],
  borderTop: `1px solid ${gr.color.line2}`,
};
const listSx = { minHeight: 0, overflowY: "auto", pb: gr.space[2] };

// ⋮ on each group (groups list) and next to the phone picker: "Group Detail" opens its popup, "Leave Group"
// asks to confirm, as before.
export function GroupMenuButton({ onGroupDetail, onLeaveGroup, disabled }) {
  const [anchor, setAnchor] = useState(null);
  const run = (action) => () => {
    setAnchor(null);
    action();
  };
  return (
    <>
      <GrIconButton
        variant="ghost"
        size="sm"
        aria-label="Group options"
        aria-haspopup="true"
        aria-expanded={Boolean(anchor)}
        disabled={disabled}
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        <GrIcon icon={icons.more} />
      </GrIconButton>
      <GrMenu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
      >
        <GrMenuItem onClick={run(onGroupDetail)}>
          <GrIcon icon={icons.info} size="md" />
          Group Detail
        </GrMenuItem>
        <GrMenuItem danger onClick={run(onLeaveGroup)}>
          <GrIcon icon={icons.logOut} size="md" />
          Leave Group
        </GrMenuItem>
      </GrMenu>
    </>
  );
}

GroupMenuButton.propTypes = {
  onGroupDetail: PropTypes.func.isRequired,
  onLeaveGroup: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

// Groups list beside the feed (large screens): General Community first, then the member's groups.
export default function GroupsFeeds({
  data,
  onGroupDetail,
  onLeaveGroup,
  groupDetailLoading,
  sx,
}) {
  const { selectedGroup, setSelectedGroup } = useContentSetting();

  const handleGroupFeed = (value) => {
    localStorage.setItem("selectedGroup", value?._id || "general");
    setSelectedGroup(value?._id || "");
  };
  const logo = get_app_logo();

  return (
    <GrCard padding="sm" sx={{ ...cardSx, width: "100%", ...sx }}>
      <Box sx={listSx}>
        <GrCardHeader as="h2" title="General Community" sx={headerSx} />
        <GrChatListRow
          src={logo}
          name="General Community"
          selected={!selectedGroup}
          onClick={() => handleGroupFeed("")}
        />
        <GrCardHeader as="h2" title="Groups" sx={groupsHeaderSx} />
        {data && data.length > 0 ? (
          data.map((item) => (
            <GrChatListRow
              key={item._id}
              src={s3baseUrl + item?.image?.thumbnail_1}
              name={item.title}
              selected={selectedGroup === item._id}
              onClick={() => handleGroupFeed(item)}
              action={
                onGroupDetail ? (
                  <GroupMenuButton
                    onGroupDetail={() => onGroupDetail(item._id)}
                    onLeaveGroup={() => onLeaveGroup(item)}
                    disabled={groupDetailLoading}
                  />
                ) : null
              }
            />
          ))
        ) : (
          <GrEmptyState
            compact
            icon={<GrIcon icon={icons.people} />}
            title="No Groups Available"
          />
        )}
      </Box>
    </GrCard>
  );
}

GroupsFeeds.propTypes = {
  data: PropTypes.array,
  onGroupDetail: PropTypes.func,
  onLeaveGroup: PropTypes.func,
  groupDetailLoading: PropTypes.bool,
  sx: PropTypes.object,
};
